import React, { useEffect, useState, useRef } from "react";
import api from "../api";
import CategoryTreeMenu from "../components/Shop/CategoryTreeMenu";
import Button from "../components/Button";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import { Outlet, Link } from "react-router-dom";
import { useShopFilter } from "../context/ShopFilterContext";
import {
  FaChevronDown, FaUser, FaSearch, FaShoppingCart,
  FaSignOutAlt, FaHome, FaThLarge
} from "react-icons/fa";
import MiniCart from "../components/Basket/MiniCart";
import { useCart } from "../features/Cart/useCart";
import ShopFooter from "./ShopFooter";
import { useAuthStore } from "../store/authStore";
import { useCartStore } from "../store/cartStore";
import {toPersianNumbers} from "../utils/toPersianNumbers";

export default function ShopLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userRole = useAuthStore((state) => state.role);
  const logout = useAuthStore((state) => state.logout);
  const hydrate = useAuthStore((state) => state.hydrate);

  const disableMiniCart = pathname.startsWith("/cart");

  useEffect(() => { hydrate(); }, [hydrate]);
  useCart({ enabled: isAuthenticated });

  const { searchTerm, setSearchTerm, resetFilters } = useShopFilter();
  const [tree, setTree] = useState([]);
  const [hoveredCategoryId, setHoveredCategoryId] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const accountRef = useRef(null);

  const cart = useCartStore((state) => state.cart);
  const itemsCount = cart?.items_count ?? 0;

  // بستن منو با کلیک خارج (دسکتاپ)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    api.get("products/shopping/categories/tree/")
      .then((res) => {
        setTree(res.data);
        if (res.data.length > 0 && !hoveredCategoryId) {
          setHoveredCategoryId(res.data[0].id);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    resetFilters();
    navigate(`/shopping/product-list/?${params}`);
  };

  const getPanelLinkByRole = () => {
    const roles = { admin: "/admin-panel", supplier: "/supplier-panel", customer: "/customer-panel" };
    return roles[userRole] || "/customer-panel";
  };

  const mobileNavLinkClass = ({ isActive }) =>
    `flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
      isActive ? "text-[#1E3A5F]" : "text-gray-500"
    }`;

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 font-['Vazirmatn']">

      {/* ============ هدر ثابت (Sticky Header) ============ */}
      {/* در موبایل فیکس می‌شود، در دسکتاپ حالت عادی دارد */}
      <header className="sticky top-0 z-[101] md:static bg-white md:bg-transparent border-b border-gray-100 md:border-none shadow-sm md:shadow-none">
        <div className="container mx-auto px-4 py-3">
          <div className="rounded-2xl md:border md:border-gray-200 bg-white md:p-4 md:shadow-sm">
            <div className="flex items-center gap-3 md:gap-4">

              {/* لوگو */}
              <div className="relative flex items-center">
                <Link to="/" className="shrink-0">
                  <img
                      src="/images/logo.png"
                      alt="Sazin"
                      className="h-9 w-9 md:h-11 md:w-11 rounded-lg object-contain"
                  />
                </Link>

                <CategoryTreeMenu
                    tree={tree}
                    hoveredCategoryId={hoveredCategoryId}
                    setHoveredCategoryId={setHoveredCategoryId}
                    isMenuOpen={isMenuOpen}
                    setIsMenuOpen={setIsMenuOpen}
                />
              </div>


              {/* باکس و دکمه جستجو (ادغام شده برای موبایل) */}
              <form onSubmit={handleSearchSubmit} className="flex flex-1 items-stretch">
                <div className="relative flex-1 group">
                  <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="جستجو در بازارگاه کالای ساختمانی..."
                      className="h-11 w-full rounded-lg md:rounded-lg border border-gray-300 bg-gray-50 pr-3 pl-12 text-sm outline-none transition-all focus:border-[#1E3A5F] focus:bg-white"
                  />
                  {/* آیکون جستجو که نقش دکمه سابمیت را دارد */}
                  <button type="submit"
                          className="absolute left-0 top-0 flex h-full w-12 items-center justify-center text-gray-400 hover:text-[#1E3A5F]">
                    <FaSearch className="text-lg"/>
                  </button>
                </div>
                {/* دکمه جستجوی دسکتاپ */}
                <Button variant="primary" type="submit" className="hidden md:flex h-11 rounded-lg px-6 mr-[8px]">
                  جستجو
                </Button>
              </form>

              {/* بخش اکشن‌ها (فقط دسکتاپ) */}
              <div className="hidden md:flex items-center gap-3">
                {!isAuthenticated && (
                    <Button variant="secondary" onClick={() => navigate("/user-login")}>ورود</Button>
                )}
                <MiniCart disabled={disableMiniCart}>
                  <Button variant="secondary" className="!px-3 h-11"><FaShoppingCart/></Button>
                </MiniCart>
                {isAuthenticated && (
                    <div className="relative" ref={accountRef}>
                      <button onClick={() => setShowAccountMenu(!showAccountMenu)}
                              className="flex h-11 items-center gap-2 rounded-lg bg-[#1E3A5F] px-4 text-[#FFC107]">
                        <FaUser/> <FaChevronDown className="text-xs"/>
                      </button>
                      {showAccountMenu && (
                          <div
                              className="absolute left-0 z-50 mt-2 w-48 rounded-xl border border-gray-100 bg-white py-2 shadow-lg">
                            <Link to={getPanelLinkByRole()} className="block px-4 py-2 text-sm">حساب کاربری</Link>
                            <button onClick={logout} className="w-full text-right px-4 py-2 text-sm text-red-600">خروج
                            </button>
                          </div>
                      )}
                    </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4">
        {/* محتوای اصلی - Margin پایین برای نوار موبایل */}
        <main className="pb-24 md:pb-8">
          <Outlet />
        </main>

        {/* فوتر (در همه نسخه‌ها) */}
        <ShopFooter />
      </div>

      {/* ============ نوار ناوبری پایین موبایل (ثابت) ============ */}
      <nav className="fixed bottom-0 left-0 right-0 z-[100] flex h-16 items-center justify-around border-t border-gray-200 bg-white px-2 md:hidden">
        <NavLink to="/" className={mobileNavLinkClass}>
          <FaHome className="text-xl" />
          <span>خانه</span>
        </NavLink>

        {/* دکمه باز کردن منوی دسته‌بندی */}
        <button
          onClick={() => setIsMenuOpen(true)}
          className="flex flex-col items-center justify-center gap-1 text-[11px] font-medium text-gray-500"
        >
          <FaThLarge className="text-xl" />
          <span>دسته‌بندی</span>
        </button>

        <NavLink to="/cart" className={mobileNavLinkClass}>
          <span className="relative">
            <FaShoppingCart className="text-xl" />
            {itemsCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {toPersianNumbers(itemsCount)}
              </span>
            )}
          </span>
          <span>سبد خرید</span>
        </NavLink>



        <NavLink to={isAuthenticated ? getPanelLinkByRole() : `/user-login`} className={mobileNavLinkClass}>
          <FaUser className="text-xl" />
          <span>{isAuthenticated ? "حساب من" : "ورود"}</span>
        </NavLink>
      </nav>
    </div>
  );
}
